import { DEFAULT_SITE } from './siteDefaults';

const FALLBACK_VIDEO =
  'https://assets.mixkit.co/videos/preview/mixkit-aerial-view-of-green-fields-and-trees-4427-large.mp4';
const FALLBACK_POSTER =
  'https://res.cloudinary.com/dnw8qhp3h/image/upload/v1777804247/rafid-seed/admin/u2cu0nlo55bxxufhxrmf.jpg';

function strPrefer(a, b, fallback) {
  if (typeof a === 'string' && a.trim()) return a.trim();
  if (typeof b === 'string' && b.trim()) return b.trim();
  return typeof fallback === 'string' ? fallback : '';
}

function normalizeYearList(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((y) => (typeof y === 'string' ? y.trim() : y != null ? String(y).trim() : ''))
    .filter(Boolean);
}

const JOURNEY_DEFAULT_YEARS = ['2010', '2015', '2020', '2025'];

function resolveAboutJourney(site) {
  const d =
    DEFAULT_SITE.about && typeof DEFAULT_SITE.about === 'object' && DEFAULT_SITE.about.journey
      ? DEFAULT_SITE.about.journey
      : {};
  const a =
    site?.about && typeof site.about === 'object' && site.about.journey ? site.about.journey : {};
  const yearsFromApi = normalizeYearList(a.timelineYears);
  const yearsFromDefault = normalizeYearList(d.timelineYears);
  const timelineYears = yearsFromApi.length ? yearsFromApi : yearsFromDefault.length ? yearsFromDefault : JOURNEY_DEFAULT_YEARS;

  return {
    sectionLabel: strPrefer(a.sectionLabel, d.sectionLabel, 'Our JOURNEY'),
    title: strPrefer(
      a.title,
      d.title,
      'Growing excellence across every field'
    ),
    body: strPrefer(
      a.body,
      d.body,
      'Nurturing quality in every field—we combine advanced research with practical agronomy to deliver smarter seed solutions for Bangladeshi growers, with varieties bred for yield, resilience, and real field conditions. From trial plots to nationwide distribution, we focus on transparency, farmer training, and long-term partnerships so every hectare can perform at its best.'
    ),
    visualUrl: strPrefer(
      a.visualUrl,
      d.visualUrl,
      'https://res.cloudinary.com/dnw8qhp3h/image/upload/v1777808495/rafid-seed/admin/vfvyli1o6vpyuc5f2may.jpg'
    ),
    timelineYears,
    accentImageUrl: strPrefer(
      a.accentImageUrl,
      d.accentImageUrl,
      'https://images.unsplash.com/photo-1594282486552-05b4b80a6e4a?w=280&q=85'
    ),
  };
}

function resolveAboutMissionVision(site) {
  const d =
    DEFAULT_SITE.about && typeof DEFAULT_SITE.about === 'object' && DEFAULT_SITE.about.missionVision
      ? DEFAULT_SITE.about.missionVision
      : {};
  const a =
    site?.about && typeof site.about === 'object' && site.about.missionVision
      ? site.about.missionVision
      : {};
  return {
    sectionEyebrow: strPrefer(a.sectionEyebrow, d.sectionEyebrow, 'Our mission / vision'),
    sectionTitle: strPrefer(
      a.sectionTitle,
      d.sectionTitle,
      "Why we exist—and where we're headed"
    ),
    missionTitle: strPrefer(a.missionTitle, d.missionTitle, 'Our mission'),
    missionBody: strPrefer(
      a.missionBody,
      d.missionBody,
      'Equip farmers with reliable, high-performing seed and honest agronomic guidance—so every planting season improves yield, income, and confidence in the field. We commit to rigorous quality control, transparent sourcing, and responsive support from trial to harvest.'
    ),
    visionTitle: strPrefer(a.visionTitle, d.visionTitle, 'Our vision'),
    visionBody: strPrefer(
      a.visionBody,
      d.visionBody,
      "Become the region's most trusted seed partner: a company where research, distribution, and farmer success move together—expanding sustainable varieties, strengthening rural livelihoods, and helping agriculture adapt with dignity and data-informed practice."
    ),
  };
}

function normalizeManagementMembers(list, defaults) {
  const fromApi = Array.isArray(list)
    ? list
        .map((m) => {
          if (!m || typeof m !== 'object') return null;
          const id = typeof m.id === 'string' && m.id.trim() ? m.id.trim() : '';
          const name = typeof m.name === 'string' ? m.name.trim() : '';
          const role = typeof m.role === 'string' ? m.role.trim() : '';
          const bio = typeof m.bio === 'string' ? m.bio.trim() : '';
          const image = typeof m.image === 'string' ? m.image.trim() : '';
          if (!name || !role || !bio) return null;
          return {
            id: id || `team-${name.slice(0, 12)}`,
            name,
            role,
            bio,
            image,
          };
        })
        .filter(Boolean)
    : [];
  if (fromApi.length) return fromApi;
  const d = Array.isArray(defaults) ? defaults : [];
  return d
    .map((m) => {
      if (!m || typeof m !== 'object') return null;
      const id = typeof m.id === 'string' && m.id.trim() ? m.id.trim() : '';
      const name = typeof m.name === 'string' ? m.name.trim() : '';
      const role = typeof m.role === 'string' ? m.role.trim() : '';
      const bio = typeof m.bio === 'string' ? m.bio.trim() : '';
      if (!name || !role || !bio) return null;
      return {
        id: id || `team-${name.slice(0, 12)}`,
        name,
        role,
        bio,
        image: typeof m.image === 'string' ? m.image.trim() : '',
      };
    })
    .filter(Boolean);
}

function resolveAboutManagementPanel(site) {
  const d =
    DEFAULT_SITE.about && typeof DEFAULT_SITE.about === 'object' && DEFAULT_SITE.about.managementPanel
      ? DEFAULT_SITE.about.managementPanel
      : {};
  const a =
    site?.about && typeof site.about === 'object' && site.about.managementPanel
      ? site.about.managementPanel
      : {};
  const defaultMembers = Array.isArray(d.members) ? d.members : [];
  return {
    sectionEyebrow: strPrefer(a.sectionEyebrow, d.sectionEyebrow, 'Management panel'),
    sectionTitle: strPrefer(
      a.sectionTitle,
      d.sectionTitle,
      'Leadership guiding every season'
    ),
    intro: strPrefer(
      a.intro,
      d.intro,
      'Our management panel brings together agronomy, operations, and commercial leadership to guide research priorities, field programs, and grower support across the country.'
    ),
    members: normalizeManagementMembers(a.members, defaultMembers),
  };
}

/** Accept 11-char id or youtu.be / watch?v= URLs */
export function parseYoutubeVideoId(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return '';
  const t = raw.trim();
  const fromQuery = t.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (fromQuery) return fromQuery[1];
  const fromShort = t.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (fromShort) return fromShort[1];
  if (/^[a-zA-Z0-9_-]{11}$/.test(t)) return t;
  return '';
}

/** About section copy and media from API / defaults; YouTube id can come from env if unset in JSON. */
export function resolveAbout(site) {
  const d = DEFAULT_SITE.about && typeof DEFAULT_SITE.about === 'object' ? DEFAULT_SITE.about : {};
  const a = site?.about && typeof site.about === 'object' ? site.about : {};
  const envYt = parseYoutubeVideoId(process.env.REACT_APP_ABOUT_YOUTUBE_VIDEO_ID || '');

  const ytFromSite = parseYoutubeVideoId(a.youtubeVideoId);
  const ytFromDefault = parseYoutubeVideoId(d.youtubeVideoId);
  const youtubeVideoId = ytFromSite || ytFromDefault || envYt;

  const line1 = strPrefer(a.headingLine1, d.headingLine1, 'Innovating Agriculture,');
  const line2 = strPrefer(a.headingLine2, d.headingLine2, 'Sustaining Our Future');
  const iframeTitle = strPrefer(
    a.youtubeIframeTitle,
    d.youtubeIframeTitle,
    `${line1} ${line2}`.trim()
  );

  const pageHeroImage = strPrefer(a.pageHeroImage, d.pageHeroImage, '');

  return {
    label: strPrefer(a.label, d.label, 'About Us'),
    pageHeroImage,
    headingLine1: line1,
    headingLine2: line2,
    intro: strPrefer(
      a.intro,
      d.intro,
      'Rafid Seed works with growers and partners to deliver quality seed and practical support.'
    ),
    videoUrl: strPrefer(a.videoUrl, d.videoUrl, FALLBACK_VIDEO),
    videoPosterUrl: strPrefer(a.videoPosterUrl, d.videoPosterUrl, FALLBACK_POSTER),
    videoAriaLabel: strPrefer(a.videoAriaLabel, d.videoAriaLabel, ''),
    youtubeVideoId,
    youtubeIframeTitle: iframeTitle,
    sidebarText: strPrefer(
      a.sidebarText,
      d.sidebarText,
      'Agriculture nurtures life through organic growth, sustainable farming.'
    ),
    sidebarCtaLabel: strPrefer(a.sidebarCtaLabel, d.sidebarCtaLabel, 'About Us'),
    sidebarCtaHref: strPrefer(a.sidebarCtaHref, d.sidebarCtaHref, '#about'),
    journey: resolveAboutJourney(site),
    missionVision: resolveAboutMissionVision(site),
    managementPanel: resolveAboutManagementPanel(site),
  };
}
